import { useCallback, useEffect, useState } from 'react';
import { Mail, Search, UserPlus } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import type { UserStatusKey } from '../../lib/user-status.ts';
import { roleLabel, typeLabel } from './derive.ts';
import { UNASSIGNED_CUSTOMER_FILTER } from './filter.ts';
import type {
  ResolvedCustomer,
  RoleStateKind,
  UsersTableFilters,
  UsersTableScope,
  UserTypeState,
} from './types.ts';
import type { UsersTableStateOptions } from './useUsersTable.ts';
import type { UsersTablePermissions } from './UserRowActions.tsx';

/**
 * "No filter" sentinel for the selects. Radix rejects an empty option value, and
 * the URL expresses "no filter" by omitting the key, so the sentinel never
 * leaves this file.
 */
const ALL = 'all';

/** ms of quiet before a keystroke reaches the URL. */
const SEARCH_DEBOUNCE_MS = 300;

export interface UsersTableToolbarProps {
  scope: UsersTableScope;
  filters: UsersTableFilters;
  setFilters: (patch: Partial<UsersTableFilters>) => void;
  clear: () => void;
  /** True when any filter differs from its default, i.e. Clear is meaningful. */
  isDirty: boolean;
  /** The customers present in the rows currently in scope. */
  customerOptions: readonly ResolvedCustomer[];
  /**
   * The Type, Role and Status states present in the rows currently in scope,
   * each already in its canonical order. Like `customerOptions`, these come
   * from the data rather than from the vocabulary, so a state nothing has is
   * not offered; the "All …" option below is added here and is always present.
   */
  stateOptions: UsersTableStateOptions;
  can: UsersTablePermissions;
  onCreateUser: () => void;
  /** Opens the Send Test Email dialog; shown alongside Create User. */
  onSendTestEmail?: () => void;
  /** Controls are inert while the users query is in flight. */
  disabled?: boolean;
}

/**
 * Search, four filter selects, Clear and Create User.
 *
 * Everything here writes through `useUsersTableFilters`, so every control is a
 * shareable URL and any change resets the page in the same write. The search
 * input keeps its own state and reaches the URL on a trailing debounce, so
 * typing does not produce one history replace per keystroke.
 */
export function UsersTableToolbar(props: UsersTableToolbarProps) {
  const {
    scope,
    filters,
    setFilters,
    clear,
    isDirty,
    customerOptions,
    stateOptions,
    can,
    onCreateUser,
    onSendTestEmail,
    disabled = false,
  } = props;

  const { search } = filters;
  const [searchText, setSearchText] = useState(search);

  // The URL is the source of truth: when it changes under us (Clear, back
  // button, a fresh deep link) the input follows it.
  useEffect(() => setSearchText(search), [search]);

  useEffect(() => {
    if (searchText === search) return;
    const timer = setTimeout(() => setFilters({ search: searchText }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText, search, setFilters]);

  const onCustomerChange = useCallback((value: string) => {
    if (value === ALL) return setFilters({ customer: null });
    if (value === UNASSIGNED_CUSTOMER_FILTER) {
      return setFilters({ customer: UNASSIGNED_CUSTOMER_FILTER });
    }
    setFilters({ customer: Number(value) });
  }, [setFilters]);

  return (
    <div className='flex items-center gap-2 flex-wrap'>
      <div className='relative flex-1 min-w-[200px]'>
        <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
        <Input
          placeholder='Search users by name, email, or customer...'
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          className='pl-8'
          disabled={disabled}
          aria-label='Search users'
        />
      </div>

      {scope.mode === 'all' && (
        <Select
          value={filters.type ?? ALL}
          onValueChange={(value) =>
            setFilters({ type: value === ALL ? null : value as UserTypeState })}
          disabled={disabled}
        >
          <SelectTrigger className='w-[140px]' aria-label='Type'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All types</SelectItem>
            {stateOptions.types.map((type) => (
              <SelectItem key={type} value={type}>{typeLabel(type)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {scope.mode === 'all' && (
        <Select
          value={filters.customer === null ? ALL : String(filters.customer)}
          onValueChange={onCustomerChange}
          disabled={disabled}
        >
          <SelectTrigger className='w-[200px]' aria-label='Customer'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All customers</SelectItem>
            <SelectItem value={UNASSIGNED_CUSTOMER_FILTER}>Unassigned</SelectItem>
            {customerOptions.map((customer) => (
              <SelectItem key={customer.id} value={String(customer.id)}>
                {customer.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Select
        value={filters.role ?? ALL}
        onValueChange={(value) =>
          setFilters({ role: value === ALL ? null : value as RoleStateKind })}
        disabled={disabled}
      >
        <SelectTrigger className='w-[180px]' aria-label='Role'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All roles</SelectItem>
          {
            /*
            Labels come from `roleLabel`, the same function the Role cell uses,
            so an option can never read differently from the cells it selects.
            The one exception is `none`, whose cell is an em dash — unusable as
            an option label, so the filter spells it "None".
          */
          }
          {stateOptions.roles.map((state) => (
            <SelectItem key={state.kind} value={state.kind}>
              {state.kind === 'none' ? 'None' : roleLabel(state)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.status ?? ALL}
        onValueChange={(value) =>
          setFilters({ status: value === ALL ? null : value as UserStatusKey })}
        disabled={disabled}
      >
        <SelectTrigger className='w-[200px]' aria-label='Status'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All Statuses</SelectItem>
          {stateOptions.statuses.map((option) => (
            <SelectItem key={option.key} value={option.key}>{option.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {isDirty && (
        <Button variant='ghost' size='sm' onClick={clear}>
          Clear
        </Button>
      )}

      {can.create && (
        <div className='ml-auto flex gap-2'>
          {onSendTestEmail && (
            <Button variant='outline' onClick={onSendTestEmail}>
              <Mail className='mr-2 h-4 w-4' />
              Send Test Email
            </Button>
          )}
          <Button onClick={onCreateUser}>
            <UserPlus className='mr-2 h-4 w-4' />
            Create User
          </Button>
        </div>
      )}
    </div>
  );
}
